import { useCallback, useContext, useState } from 'react';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { ChatViewType, ChatViewTypeContext, SideBarExpandedContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ActiveChatSessionContext } from '@trinity/chat-components/components/Chat/context';
import { ChatTabsContext } from '@trinity/chat-components/components/Chat.History/context';
import { useLoadChatInstance } from '@trinity/chat-components/components/Chat.History/hooks';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { ChatStateDispatchContext } from '../Chat.State/context';

type Props = unknown;

export const ChatTabs = (props: Props) => {
  const { index, items: tabs } = useContext(ChatTabsContext);
  const session = useContext(ActiveChatSessionContext);
  const chatViewType = useContext(ChatViewTypeContext);
  const [sidebarExpanded] = useContext(SideBarExpandedContext) || [true];
  const loadInstance = useLoadChatInstance();
  const dispatch = useContext(ChatStateDispatchContext);
  const brandIdentifier = useActiveBrandIdentifier();

  const handleChange = useCallback((e: React.SyntheticEvent, newValue: number) => {
    dispatch({
      type: 'chat-tabs-index-changed',
      payload: { index: newValue },
    });
  }, [dispatch]);

  const setChatInstance = useCallback((identifier: string) => {
    if (identifier === session.identifier) return;

    loadInstance(identifier);
  }, [
    loadInstance,
    session.identifier,
  ]);

  const handleClickTab = useCallback((e: React.SyntheticEvent, tab: TabData) => {
    dispatch({
      type: 'chat-tabs-index-changed',
      payload: { index: tab.index },
    });
    setChatInstance(tab.item.identifier);
  }, [
    setChatInstance,
    dispatch,
  ]);

  const handleCloseTab = useCallback((e: React.SyntheticEvent, tab: TabData) => {
    e.stopPropagation();

    const nextTabs = tabs.filter((_, i) => i !== tab.index).map((x, i) => ({ ...x, index: i }));

    dispatch({
      type: 'chat-tabs-items-changed',
      payload: {
        items: nextTabs.map(m => m.id),
      },
    });

    const activeTab = tabs[index];
    const tabWasActive = activeTab?.id === tab.item.id;

    if (!tabWasActive && activeTab) {
      const nextIndex = nextTabs.findIndex(x => x.id === activeTab?.id);

      dispatch({
        type: 'chat-tabs-index-changed',
        payload: { index: nextIndex },
      });
    }

    if (tabWasActive) {
      const nextActive = nextTabs[tab.index - 1] || nextTabs[tab.index + 1];
      const nextIndex = nextTabs.findIndex(x => x.id === nextActive?.id);
      const resolvedIndex = Number.isInteger(nextIndex) ? nextIndex : -1;

      dispatch({
        type: 'chat-tabs-index-changed',
        payload: { index: resolvedIndex },
      });

      if (resolvedIndex > -1) {
        const nextTab = nextTabs[resolvedIndex];
        setChatInstance(nextTab.identifier);
      }
    }

    // When closing the last tab, start a new chat with the current context
    if (nextTabs.length === 0) {
      dispatch({
        type: 'new-chat-started',
        payload: {
          context: session.context,
          brandIdentifier,
        },
      });
    }
  }, [setChatInstance, tabs, index, session.context, dispatch, brandIdentifier]);

  const isModal = chatViewType === ChatViewType.Modal;
  let maxVisibleTabs: number;
  if (isModal) {
    maxVisibleTabs = sidebarExpanded ? 1 : 2;
  } else {
    maxVisibleTabs = 5;
  }
  const needsDropdown = tabs.length > maxVisibleTabs;
  const visibleTabs = needsDropdown ? tabs.slice(0, maxVisibleTabs) : tabs;

  let tabValue: number | false;
  if (needsDropdown && index >= maxVisibleTabs) {
    tabValue = false;
  } else if (index === -1) {
    tabValue = 0;
  } else {
    tabValue = index;
  }

  return (
    <>
      <TabsStyled
        value={tabValue}
        variant={needsDropdown ? 'standard' : 'scrollable'}
        scrollButtons={!needsDropdown && tabs.length ? 'auto' : false}
        allowScrollButtonsMobile={!needsDropdown}
        onChange={handleChange}
        sx={{
          ...(needsDropdown && {
            '& .MuiTabs-flexContainer': {
              minWidth: 'fit-content',
            },
          }),
        }}>
        {visibleTabs.map((x, i) =>
          <ChatTab
            active={index === i}
            key={x.identifier}
            label={x.name}
            index={i}
            item={x}
            onClick={handleClickTab}
            onClose={handleCloseTab} />)}
        {visibleTabs.length > 0 && <TabIndicator count={visibleTabs.length} index={Math.min(index, visibleTabs.length - 1)} />}
      </TabsStyled>
      {needsDropdown && (
        <TabDropdown items={
          tabs.slice(maxVisibleTabs).map((x, i) => {
            const absoluteIndex = i + maxVisibleTabs;
            return {
              active: index === absoluteIndex,
              item: x,
              label: x.name || 'Untitled Chat',
              index: absoluteIndex,
              onClick: handleClickTab,
              onClose: handleCloseTab,
            };
          })
        } />
      )}
    </>
  );
};

ChatTabs.displayName = 'Chat.Tabs';

type ChatTabProps = {
  active?: boolean;
  index: number;
  label?: string | null;
  item: { id: number; identifier: string; name: string };
  onClick: (e: React.SyntheticEvent, tab: TabData) => void;
  onClose: (e: React.SyntheticEvent, tab: TabData) => void;
};

const ChatTab = ({ label, item, ...props }: ChatTabProps) => {
  return (
    <TabStyled
      active={props.active}
      disableRipple
      onClick={e => props.onClick(e, { item, index: props.index })}
      iconPosition="end"
      icon={
        <IconButtonStyled
          disableRipple
          onClick={e => props.onClose(e, { item, index: props.index })}>
          <CloseIcon />
        </IconButtonStyled>
      }
      label={<TabLabel title={label ?? 'Untitled Chat'}>{label ?? 'Untitled Chat'}</TabLabel>} />
  );
};

type TabData = {
  index: number;
  item: { id: number; identifier: string; name: string };
};

const TabsStyled = styled(Tabs)(({ theme }) => ({
  minHeight: 43,
  maxWidth: 940,
  height: 43,
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.sentiment.primary.main,
  },
}));

type TabProps = {
  active?: boolean;
};

const TAB_WIDTH = 180;

const TabStyled = styled(Tab)<TabProps>(({ active, theme }) => ({
  padding: '0 16px',
  height: 43,
  minHeight: 43,
  maxHeight: 43,
  color: active ? theme.palette.sentiment.primary.main : theme.palette.black.main,
  opacity: active ? 1 : undefined,
  fontFamily: theme.fonts.semiBold,
  width: TAB_WIDTH,
  maxWidth: TAB_WIDTH,
  minWidth: TAB_WIDTH,
  borderRight: `1px solid ${theme.palette.gray.light4}`,
  borderTopRightRadius: '5px',
  borderTopLeftRadius: '5px',
  borderTop: `1px solid ${theme.palette.gray.light4}`,

  '&:first-of-type': {
    borderLeft: `1px solid ${theme.palette.gray.light4}`,
  },

  '&:hover': {
    transition: 'color 180ms ease-in',
    color: theme.palette.sentiment.primary.main,
  },
}));

const IconButtonStyled = styled(IconButton)(({ theme }) => ({
  padding: 0,
  color: theme.palette.gray.main,

  '&:hover': {
    transition: 'color 180ms ease-in',
    color: theme.palette.sentiment.primary.main,
  },
}));

const TabLabel = styled.div(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: theme.fonts.semiBold,
  fontSize: 13,
}));

const TabIndicator = (props: { count: number; index: number }) => {
  const left = TAB_WIDTH * props.index;
  const width = TAB_WIDTH;

  return (
    <Indicator
      style={{ left, width }} />
  );
};

const Indicator = styled.div(({ theme }) => ({
  position: 'absolute',
  height: '2px',
  backgroundColor: theme.palette.sentiment.primary.main,
  transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  zIndex: 1,
  bottom: 0,
}));

const TabDropdown = (props: { items: ChatTabProps[] }) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((previousOpen) => !previousOpen);
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  const canBeOpen = open && Boolean(anchorEl);
  const id = canBeOpen ? 'spring-popper' : undefined;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div>
        <DropdownMenuButton
          type="button"
          aria-describedby={id}
          onClick={handleClick}>
          <ExpandMoreIcon style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }} />
        </DropdownMenuButton>
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          disablePortal={false}
          style={{ zIndex: 1300 }}>
          <DropdownMenu
            items={props.items}
            onClose={() => setOpen(false)} />
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

const DropdownMenuButton = styled.button(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 43,
  width: 50,
  color: theme.palette.black.main,
}));

const DropdownMenuItem = (props: ChatTabProps) => {
  const { label, item, index, onClick, onClose } = props;

  return (
    <DropdownItemStyled
      active={props.active}
      disableRipple
      onClick={e => onClick(e, { item, index })}>
      <Typography variant="inherit" noWrap>
        {label}
      </Typography>
      <ListItemIcon>
        <CloseIcon
          fontSize="small"
          onClick={e => onClose(e, { item, index })} />
      </ListItemIcon>
    </DropdownItemStyled>
  );
};

const DropdownMenu = (props: { items: ChatTabProps[], onClose: () => void }) => {

  const closeAfterClick = useCallback((fn: ChatTabProps['onClick']) => {
    return (e: React.MouseEvent, tab: ChatTabProps) => {
      fn(e, tab);
      props.onClose();
    };
  }, [props]);

  const closeIfEmpty = useCallback((fn: ChatTabProps['onClose']) => {
    return (e: React.MouseEvent, tab: ChatTabProps) => {
      fn(e, tab);
      if (props.items.length === 1) {
        props.onClose();
      }
    };
  }, [props]);

  return (
    <Paper sx={{
      width: 230,
      backgroundColor: 'white',
      border: '1px solid #ccc',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    }}>
      <MenuList>
        {props.items.map(x =>
          <DropdownMenuItem
            active={x.active}
            key={x.index}
            label={x.label}
            item={x.item}
            index={x.index}
            onClick={closeAfterClick(x.onClick)}
            onClose={closeIfEmpty(x.onClose)} />)}
      </MenuList>
    </Paper>
  );
};

const DropdownItemStyled = styled(MenuItem)<{ active?: boolean }>(({ active, theme }) => ({
  justifyContent: 'space-between',
  backgroundColor: (() => {
    if (!active) return undefined;
    if (theme.isTrinityThemed) return theme.palette.sentiment.primary.light;
    return theme.palette.btnPrimary.bg;
  })(),

  '&:hover': {
    backgroundColor: theme.isTrinityThemed ? theme.palette.sentiment.primary.light : theme.palette.btnPrimary.bg,
  },
}));